// OWNED BY THE VISIT AUTHOR. Nothing outside this namespace belongs here, and
// nothing here belongs in another file: this module exists so that the VISIT
// work and the rest of the app can be written at the same time without two
// people editing one map.
export const visit = {
  // ── visits ───────────────────────────────────────────────────────────
  'visit.number': 'Visit {{n}}',
  'visit.status.open': 'Open',
  'visit.status.closed': 'Closed',
  'visit.placeholder.title': 'The visit workspace is under construction',
  'visit.placeholder.body': 'The SOAP note, diagnoses, prescription and orders arrive here soon.',
  'visit.placeholder.back': 'Back to the record',

  // ── clinical vocabulary — core.md §5.3 (used by the Visit workspace too) ──
  'clinical.dx.kind.primary': 'Primary',
  'clinical.dx.kind.secondary': 'Secondary',
  'clinical.dx.kind.differential': 'Differential',
  'clinical.dx.status.active': 'Active',
  'clinical.dx.status.resolved': 'Resolved',
  'clinical.dx.status.ruled_out': 'Ruled out',
  'clinical.rx.route.oral': 'Oral',
  'clinical.rx.route.im': 'IM',
  'clinical.rx.route.iv': 'IV',
  'clinical.rx.route.sc': 'SC',
  'clinical.rx.route.topical': 'Topical',
  'clinical.rx.route.inhaled': 'Inhaled',
  'clinical.rx.route.rectal': 'Rectal',
  'clinical.rx.route.ophthalmic': 'Ophthalmic',
  'clinical.rx.route.otic': 'Otic',
  'clinical.rx.route.nasal': 'Nasal',
  'clinical.rx.route.sublingual': 'Sublingual',
  'clinical.rx.route.other': 'Other',
  'clinical.order.type.lab': 'Lab',
  'clinical.order.type.imaging': 'Imaging',
  'clinical.order.type.procedure': 'Procedure',
  'clinical.order.type.referral': 'Referral',
  'clinical.order.type.other': 'Other',
  'clinical.order.urgency.routine': 'Routine',
  'clinical.order.urgency.urgent': 'Urgent',
  'clinical.order.urgency.stat': 'Stat',
  'clinical.order.status.ordered': 'Ordered',
  'clinical.order.status.resulted': 'Resulted',
  'clinical.order.status.cancelled': 'Cancelled',
  'clinical.origin.manual': 'Entered by you',
  'clinical.origin.ai': 'From an AI suggestion',
  'clinical.differential.likelihood.high': 'Likely',
  'clinical.differential.likelihood.medium': 'Possible',
  'clinical.differential.likelihood.low': 'Less likely',
  'clinical.interaction.severity.severe': 'Severe',
  'clinical.interaction.severity.moderate': 'Moderate',
  'clinical.interaction.severity.mild': 'Mild',
  'clinical.interaction.type.interaction': 'Drug interaction',
  'clinical.interaction.type.contraindication': 'Contraindication',
  'clinical.interaction.type.allergy': 'Allergy',
  'clinical.interaction.type.black_box': 'Black-box warning',
  'clinical.interaction.source.rule_engine_static': 'Rule table',
  'clinical.interaction.source.rule_engine_rxnorm': 'RxNorm rules',
  'clinical.interaction.source.rule_engine_contraindication': 'Contraindication rules',
  'clinical.interaction.source.rule_engine_allergy': 'Allergy rules',
  'clinical.interaction.source.rule_engine_openfda': 'openFDA',
  'clinical.interaction.source.llm': 'Language model',

  // ── the assistant (shared pieces: cost chip, voice) ──────────────────
  'assistant.cost': '{{parseh}} Parseh',
  'assistant.costBefore': 'Costs {{parseh}} Parseh',
  'assistant.costFree': 'Free',
  'assistant.balance': 'You have {{parseh}} Parseh',
  'assistant.noCredits.title': 'Not enough Parseh',
  'assistant.noCredits.body': 'Top up, or wait for next month’s free grant. Nothing you wrote was lost.',
  'assistant.noCredits.action': 'Top up Parseh',
  'assistant.voice.title': 'Dictate the history',
  'assistant.voice.body': 'Describe the patient out loud, then review what it understood before anything is saved.',
  'assistant.voice.start': 'Start dictating',
  'assistant.voice.stop': 'Stop',
  'assistant.voice.listening': 'Listening…',
  'assistant.voice.unsupported': 'This browser cannot transcribe speech. Type or paste what you dictated instead.',
  'assistant.voice.transcript': 'What was heard',
  'assistant.voice.transcriptPlaceholder': 'A 54-year-old man with crushing chest pain since this morning, known diabetic, on metformin…',
  'assistant.voice.parse': 'Turn this into a history',
  'assistant.voice.parsing': 'Reading it…',
  'assistant.voice.needsTranscript': 'There is nothing to read yet.',
  'assistant.voice.draft.title': 'Draft — nothing has been saved',
  'assistant.voice.draft.body': 'Untick anything you do not want, then confirm.',
  'assistant.voice.draft.none': 'It found nothing it could file into a section.',
  'assistant.voice.draft.confirm': 'Add the ticked entries',
  'assistant.voice.draft.confirming': 'Adding…',
  'assistant.voice.draft.discard': 'Discard the draft',
  'assistant.voice.draft.demographics': 'It also heard',
  'assistant.voice.draft.demographicsNote': 'These are not applied — edit the patient if you want them.',
  'assistant.voice.applied': '{{count}} entries were added to the history.',

  'visit.back': 'Back to the record',
  'visit.backToRecord': 'Back to the record',
  'visit.notFound': 'This visit was not found',
  'visit.end': 'End the visit',
  'visit.reopen': 'Reopen',
  'visit.cancel': 'Cancel the visit',
  'visit.elapsed': '{{h}}h {{m}}m into the visit',
  'visit.closedAt': 'Closed on {{date}}',
  'visit.save.saved': 'Saved',
  'visit.save.saving': 'Saving…',
  'visit.save.failed': 'Not saved — retry',

  'visit.tabs.aria': 'Visit sections',
  'visit.tabs.note': 'Note',
  'visit.tabs.diagnoses': 'Diagnoses',
  'visit.tabs.rx': 'Prescription',
  'visit.tabs.orders': 'Orders',

  'visit.soap.s': 'S',
  'visit.soap.o': 'O',
  'visit.soap.a': 'A',
  'visit.soap.p': 'P',
  'visit.soap.sCaption': 'History',
  'visit.soap.oCaption': 'Examination and findings',
  'visit.soap.aCaption': 'Assessment',
  'visit.soap.pCaption': 'Plan',
  'visit.soap.suggest': 'Assistant suggestion',
  'visit.soap.suggesting': 'Writing a draft…',
  'visit.soap.suggestNote': 'A draft — nothing is applied until you apply it.',
  'visit.soap.listening': 'Listening…',
  'visit.soap.dictate': 'Dictate into this section',
  'visit.soap.dictateStop': 'Stop dictating',
  'visit.soap.reviewTitle': 'Review the suggested note',
  'visit.soap.reviewEmpty': 'It suggested nothing that fits a section.',
  'visit.soap.reviewCurrent': 'What you have written',
  'visit.soap.applyReplace': 'Replace',
  'visit.soap.applyAppend': 'Add below',

  'visit.dx.title': 'Diagnosis',
  'visit.dx.icd': 'ICD code',
  'visit.dx.icdHint': 'Optional — a letter and two digits, e.g. I21.0',
  'visit.dx.icdInvalid': 'This is not shaped like an ICD code',
  'visit.dx.kind': 'Kind',
  'visit.dx.status': 'Status',
  'visit.dx.note': 'Note',
  'visit.dx.add': 'Add a diagnosis',
  'visit.dx.empty': 'No diagnosis yet',
  'visit.dx.emptyBody': 'Write what you think is going on, or ask for a differential.',
  'visit.dx.moveUp': 'Move up',
  'visit.dx.moveDown': 'Move down',
  'visit.dx.differential': 'Assistant differential',
  'visit.dx.differentialBusy': 'Thinking…',
  'visit.dx.differentialLast': 'The last differential',
  'visit.dx.differentialTitle': 'Differential diagnosis',
  'visit.dx.differentialEmpty': 'It returned no separable list — read the explanation above.',
  'visit.dx.discriminators': 'What separates it',
  'visit.dx.nextSteps': 'Next steps',
  'visit.dx.addToList': 'Add to diagnoses',
  'visit.dx.addedToList': 'Added',

  'visit.rx.drug': 'Drug',
  'visit.rx.drugHint': 'Pick from the formulary, or type a name it does not carry',
  'visit.rx.searching': 'Searching…',
  'visit.rx.freeText': 'Outside the formulary',
  'visit.rx.freeTextNote': 'Rows outside the formulary are skipped by the interaction check.',
  'visit.rx.dose': 'Dose',
  'visit.rx.route': 'Route',
  'visit.rx.frequency': 'Frequency',
  'visit.rx.duration': 'Duration',
  'visit.rx.quantity': 'Quantity',
  'visit.rx.instructions': 'Instructions',
  'visit.rx.detail': 'Directions',
  'visit.rx.add': 'Add a drug',
  'visit.rx.empty': 'Nothing prescribed yet',
  'visit.rx.emptyBody': 'Add the drugs you would prescribe in this visit.',
  'visit.rx.check': 'Check interactions',
  'visit.rx.checking': 'Checking…',
  'visit.rx.lastCheck': 'The last check',
  'visit.rx.interactionTitle': 'Interaction check',
  'visit.rx.interactionNone': 'No interaction was found',
  'visit.rx.interactionNoneBody': 'For the rows the check could see. Read the skipped list below.',
  'visit.rx.interactionSource': 'Source',
  'visit.rx.skippedTitle': 'These rows were not checked',
  'visit.rx.skippedBody': 'They were typed by hand and carry no formulary code, so the check could not look them up.',
  'visit.rx.print': 'Print the prescription',
  'visit.rx.printTitle': 'Practice prescription',
  'visit.rx.printAction': 'Print',
  'visit.rx.printFooter': 'A practice document. It is not valid and must never be dispensed.',
  'visit.rx.watermark': 'PRACTICE PRESCRIPTION — NOT VALID',
  'visit.rx.studentNumber': 'Student number',

  'visit.orders.add': 'Add an order',
  'visit.orders.fromCatalog': 'From the catalogue',
  'visit.orders.title': 'Order',
  'visit.orders.type': 'Type',
  'visit.orders.urgency': 'Urgency',
  'visit.orders.notes': 'Why / instructions',
  'visit.orders.empty': 'No orders yet',
  'visit.orders.emptyBody': 'Order the labs and imaging this visit calls for.',
  'visit.orders.pickTitle': 'Pick from the catalogue',
  'visit.orders.pickEmpty': 'Nothing matches — type the order instead.',
  'visit.orders.search': 'Search the catalogue',
  'visit.orders.recordResult': 'Record a result',
  'visit.orders.cancelOrder': 'Cancel the order',
  'visit.orders.resultTitle': 'Record the result',
  'visit.orders.resultBody': 'The result',
  'visit.orders.resultRows': 'Values',
  'visit.orders.resultAddRow': 'Add a row',
  'visit.orders.resultName': 'Test',
  'visit.orders.resultValue': 'Value',
  'visit.orders.resultUnit': 'Unit',
  'visit.orders.resultRef': 'Reference',
  'visit.orders.resultSave': 'Save the result',
  'visit.orders.resultFlag': 'Flag',
  'visit.orders.resultFlagNone': '—',
  'visit.orders.resultEmpty': 'Write a result, or enter at least one value row.',
  'visit.orders.suggest': 'Assistant suggestions',
  'visit.orders.suggestBusy': 'Thinking…',
  'visit.orders.suggestLast': 'The last suggestions',
  'visit.orders.suggestTitle': 'Suggested orders',
  'visit.orders.suggestEmpty': 'It suggested no separable orders — read the explanation above.',
  'visit.orders.suggestAdd': 'Add the ticked ({{count}})',

  'visit.rail.title': 'Record',
  'visit.rail.expand': 'Open the record rail',
  'visit.rail.collapse': 'Collapse the record rail',
  'visit.rail.allergies': 'Allergy alert',
  'visit.rail.identity': 'Details',
  'visit.rail.vitals': 'Vital signs',
  'visit.rail.vitalsAdd': 'Record',
  'visit.rail.vitalsPlaceholder': 'BP 120/80 · HR 72',
  'visit.rail.medications': 'Current medications',
  'visit.rail.problems': 'Active problems',
  'visit.rail.pmh': 'Past medical history',
  'visit.rail.labs': 'Labs',
  'visit.rail.history': 'History',
  'visit.rail.ageYears': '{{n}} years old',
  'visit.rail.noVitals': 'No vital signs recorded.',
  'visit.rail.noMedications': 'No current medications.',
  'visit.rail.noProblems': 'No active problems.',
  'visit.rail.noPmh': 'Nothing written yet.',
  'visit.rail.noLabs': 'No labs written yet.',
  'visit.rail.noHistory': 'The history is empty.',
  'visit.rail.emptyList': 'Nothing here yet.',

  'visit.copilot.title': 'Assistant',
  'visit.copilot.aria': 'Assistant sections',
  'visit.copilot.ask': 'Ask',
  'visit.copilot.differential': 'Differential',
  'visit.copilot.suggestions': 'Suggestions',
  'visit.copilot.askEmpty': 'Ask about this visit — it reads the record and the note you are writing.',
  'visit.copilot.thinking': 'Thinking…',
  'visit.copilot.placeholder': 'Ask about this visit',
  'visit.copilot.send': 'Ask',
  'visit.copilot.offline': 'Offline — the assistant cannot be reached.',
  'visit.copilot.open': 'Open the assistant',
  'visit.copilot.collapse': 'Collapse the assistant',
  'visit.copilot.refresh': 'Refresh',
  'visit.copilot.openCards': 'Open the cards',
  'visit.copilot.differentialEmpty': 'No differential has been asked for in this visit.',
  'visit.copilot.soapSuggestion': 'Note suggestion',
  'visit.copilot.orderSuggestion': 'Order suggestions',
  'visit.copilot.suggestionsEmpty': 'Nothing suggested in this visit yet.',
  'visit.copilot.reopen': 'Open again',

  'visit.close.title': 'End the visit',
  'visit.close.summary': 'Visit summary',
  'visit.close.summaryHint': 'Optional. Pre-filled from the first lines of your assessment and plan.',
  'visit.close.warningsTitle': 'Before you close it',
  'visit.close.warningsNote': 'None of these prevents closing.',
  'visit.close.warning.emptyNote': 'The visit note is empty.',
  'visit.close.warning.noDiagnoses': 'No diagnosis has been recorded.',
  'visit.close.warning.openSuggestions': 'An assistant suggestion is still open and unreviewed.',
  'visit.close.ending': 'Ending…',
  'visit.close.saveFailed': 'The note could not be saved, so the visit was not closed',

  'visit.cancelConfirm.title': 'Cancel this visit?',
  'visit.cancelConfirm.body': '{{dx}} diagnoses, {{rx}} drugs and {{orders}} orders will be deleted. Vital signs and history entries stay on the record.',
  'visit.cancelConfirm.confirm': 'Delete the visit',

  'visit.offline.title': 'The service cannot be reached',
  'visit.offline.body': 'What you write is kept on this device and sent when the connection returns.',

  'visit.toast.ended': 'The visit was closed.',
  'visit.toast.cancelled': 'The visit was deleted.',
  'visit.toast.discardedDraft': 'A local draft of the note was older than the saved one and was discarded.',

  'visit.shortcuts.group': 'Visit',
  'visit.shortcuts.save': 'Save the note now',
  'visit.shortcuts.tab1': 'Note',
  'visit.shortcuts.tab2': 'Diagnoses',
  'visit.shortcuts.tab3': 'Prescription',
  'visit.shortcuts.tab4': 'Orders',
  'visit.shortcuts.send': 'Send the question',
  'visit.shortcuts.end': 'End the visit',
};

export default visit;
